import { Network, ConnectionStatus } from '@capacitor/network';

interface NetworkChangeListener {
  (status: ConnectionStatus): void;
}

class NetworkService {
  private currentStatus: ConnectionStatus | null = null;
  private listeners: NetworkChangeListener[] = [];
//   private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private async initialize() {
    try {
      // Get initial network status
      this.currentStatus = await Network.getStatus();
      this.logNetworkStatus('Application Load', this.currentStatus);

      // Listen for network status changes
      Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
        this.handleNetworkStatusChange(status);
      });

    //   this.isInitialized = true;
      console.log('NetworkService initialized successfully');
    } catch (error) {
      console.error('Failed to initialize NetworkService:', error);
    }
  }

  private handleNetworkStatusChange(status: ConnectionStatus) {
    const previousStatus = this.currentStatus;
    this.currentStatus = status;

    // Log the status change
    this.logNetworkStatusChange(previousStatus, status);

    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
  }

  private logNetworkStatus(event: string, status: ConnectionStatus) {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] Network Status (${event}):`;
    
    console.group(logMessage);
    console.log('Connected:', status.connected);
    console.log('Connection Type:', status.connectionType);
    
    if (status.connected) {
      console.log('✅ Online');
    } else {
      console.log('❌ Offline');
    }
    
    console.groupEnd();

    // Also log to a structured format for potential analytics
    this.logStructuredNetworkEvent(event, status);
  }

  private logNetworkStatusChange(previousStatus: ConnectionStatus | null, newStatus: ConnectionStatus) {
    const timestamp = new Date().toISOString();
    
    if (!previousStatus) {
      this.logNetworkStatus('Initial Status', newStatus);
      return;
    }

    const connectionChanged = previousStatus.connected !== newStatus.connected;
    const typeChanged = previousStatus.connectionType !== newStatus.connectionType;

    if (connectionChanged || typeChanged) {
      console.group(`[${timestamp}] Network Status Change:`);
      
      if (connectionChanged) {
        if (newStatus.connected) {
          console.log('🟢 Connection restored');
          console.log(`Previous: ${previousStatus.connected ? 'Online' : 'Offline'} → Current: Online`);
        } else {
          console.log('🔴 Connection lost');
          console.log(`Previous: ${previousStatus.connected ? 'Online' : 'Offline'} → Current: Offline`);
        }
      }

      if (typeChanged) {
        console.log(`Connection type changed: ${previousStatus.connectionType} → ${newStatus.connectionType}`);
      }

      console.log('Current Status:');
      console.log('- Connected:', newStatus.connected);
      console.log('- Connection Type:', newStatus.connectionType);
      
      console.groupEnd();

      // Log structured event
      this.logStructuredNetworkEvent('Status Change', newStatus, previousStatus);
    }
  }

  private logStructuredNetworkEvent(
    event: string, 
    currentStatus: ConnectionStatus, 
    previousStatus?: ConnectionStatus | null
  ) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      currentStatus: {
        connected: currentStatus.connected,
        connectionType: currentStatus.connectionType,
      },
      previousStatus: previousStatus ? {
        connected: previousStatus.connected,
        connectionType: previousStatus.connectionType,
      } : null,
      userAgent: navigator.userAgent,
    };

    // Store in localStorage for potential debugging/analytics
    try {
      const networkLogs = JSON.parse(localStorage.getItem('network_logs') || '[]');
      networkLogs.push(logEntry);
      
      // Keep only last 50 entries to prevent localStorage bloat
      if (networkLogs.length > 50) {
        networkLogs.splice(0, networkLogs.length - 50);
      }
      
      localStorage.setItem('network_logs', JSON.stringify(networkLogs));
    } catch (error) {
      console.warn('Failed to store network log:', error);
    }
  }

  // Public API methods
  public getCurrentStatus(): ConnectionStatus | null {
    return this.currentStatus;
  }

  public isOnline(): boolean {
    return this.currentStatus?.connected ?? false;
  }

  public isOffline(): boolean {
    return !this.isOnline();
  }

  public getConnectionType(): string {
    return this.currentStatus?.connectionType ?? 'unknown';
  }

  public addListener(listener: NetworkChangeListener): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public removeListener(listener: NetworkChangeListener): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  public async refreshStatus(): Promise<ConnectionStatus> {
    try {
      const status = await Network.getStatus();
      this.handleNetworkStatusChange(status);
      return status;
    } catch (error) {
      console.error('Failed to refresh network status:', error);
      throw error;
    }
  }

  // Utility method to get network logs for debugging
  public getNetworkLogs(): any[] {
    try {
      return JSON.parse(localStorage.getItem('network_logs') || '[]');
    } catch (error) {
      console.error('Failed to retrieve network logs:', error);
      return [];
    }
  }

  // Clear network logs
  public clearNetworkLogs(): void {
    try {
      localStorage.removeItem('network_logs');
      console.log('Network logs cleared');
    } catch (error) {
      console.error('Failed to clear network logs:', error);
    }
  }

  // Get network statistics
  public getNetworkStats(): {
    totalEvents: number;
    onlineEvents: number;
    offlineEvents: number;
    connectionTypes: { [key: string]: number };
  } {
    const logs = this.getNetworkLogs();
    const stats = {
      totalEvents: logs.length,
      onlineEvents: 0,
      offlineEvents: 0,
      connectionTypes: {} as { [key: string]: number },
    };

    logs.forEach((log: any) => {
      if (log.currentStatus.connected) {
        stats.onlineEvents++;
      } else {
        stats.offlineEvents++;
      }

      const connectionType = log.currentStatus.connectionType;
      stats.connectionTypes[connectionType] = (stats.connectionTypes[connectionType] || 0) + 1;
    });

    return stats;
  }
}

// Export singleton instance
export const networkService = new NetworkService();
export default networkService;